import { useState } from 'react';

import { availableRoles, type Role } from '@/modules/auth/roles';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/modules/ui/components/dialog/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/ui/components/select/Select';
import type { UserApi } from '@/modules/users/types';

interface AssignRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserApi | null;
  onSubmit: (userId: string, role: Role) => void;
  isPending: boolean;
}

export const AssignRoleDialog = ({
  open,
  onOpenChange,
  user,
  onSubmit,
  isPending,
}: AssignRoleDialogProps) => {
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(undefined);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedRole(undefined);
    }
    onOpenChange(newOpen);
  };

  const handleSubmit = () => {
    if (user && selectedRole) {
      onSubmit(user.id, selectedRole);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Assign Role to {user?.firstName} {user?.lastName}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Select
            onValueChange={(val) => setSelectedRole(val as Role)}
            value={selectedRole}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {availableRoles
                .filter((role) => !user?.roles?.includes(role))
                .map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedRole}
            loading={isPending}
          >
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
